/**
 * Component implementing one line of the JobList table.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2024-2025
 */

import { LogTag } from '@amplify3/duplo-back-end'
import { downloader } from '@/config/downloader'
import { useState } from 'react'
import { BiCoin, BiDownload, BiLineChart, BiLoaderAlt } from 'react-icons/bi'
import { IconType } from 'react-icons/lib'
import { logger } from '@/config/logger'
import { getJobInfos as getJobInfo } from '../../api/jobs'
import JobError from '../jobui/JobError'
import StatusComp from '../jobui/StatusComp'
import { getDatesDiffAsDays } from '../jobui/utils/functions'
import { ModuleConfig } from '../ModuleConfig/ModuleConfig'
import {
  getModuleByServiceName,
  getModuleDisplayName,
} from '../ModuleConfig/ModuleConfigList'
import { TimeAnalysisSeries } from '../TimeAnalysisChart'
import { fetchAuthSession } from 'aws-amplify/auth'

interface IconButtonProps {
  onClick: () => void
  isLoading: boolean
  icon: IconType
  label: string
  loading?: boolean
}

const IconButton = (props: IconButtonProps) => {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className={props.isLoading ? 'downloadBtn loadingMsg' : 'downloadBtn'}
    >
      {props.isLoading ? (
        <BiLoaderAlt className="loadingIcon" />
      ) : (
        <div>
          <props.icon />
        </div>
      )}
      {props.label}
    </button>
  )
}

interface TimeAnalysis {
  timeAnalysisIasId: string
  inputFileName: string
  isAi: boolean
  data?: any
}

interface JobResult {
  jobId: string
  report: any
  fileList: any[]
  timeAnalysisId: any
  timeAnalysisList: TimeAnalysis[] | undefined
}

interface JobInfoProps {
  job: any
  showChartsDialogVisible: (series: TimeAnalysisSeries[]) => void
}

const JobInfo = (props: JobInfoProps) => {
  const startDateTime = new Date(props.job?.job_start_datetime ?? 0)
  const uploadedFileName =
    props.job?.file_paths?.[0] !== undefined
      ? props.job.file_paths[0].split('/').pop()
      : undefined

  const [isLoading, setLoading] = useState(false)
  const [isLoadingCharts, setLoadingCharts] = useState(false)
  const [timeAnalysisSeries, setTimeAnalysisSeries] = useState<
    TimeAnalysisSeries[]
  >([])

  const [jobResult, setJobResult] = useState<JobResult | undefined>(undefined)
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)
  const module: ModuleConfig | undefined = getModuleByServiceName(
    props.job?.service_name
  )
  const moduleDisplayName = getModuleDisplayName(props.job?.service_name)

  async function getJobResult() {
    const data = await getJobInfo({ jobId: props.job?.job_id })

    const report = data?.report_info?.report
    logger.debug(LogTag.TaskList, 'report:', report)

    const fileList = data?.file_paths ?? []
    logger.debug(LogTag.TaskList, 'filePaths:', fileList)

    // Get list of Time Analysis
    const timeAnalysisList: TimeAnalysis[] = []

    if (report?.resultList) {
      for (let index = 0; index < report.resultList.length; ++index) {
        const currentResult = report.resultList.at(index)
        if (currentResult.timeAnalysis) {
          const item = {
            timeAnalysisIasId: currentResult.timeAnalysis.id,
            inputFileName: currentResult.inputFilename,
            isAi: currentResult.isAi,
          }
          timeAnalysisList.push(item)
        }
      }
    }

    const result = {
      jobId: props.job?.job_id,
      report: report,
      fileList: fileList,
      timeAnalysisId: report?.resultList?.at(0)?.timeAnalysis,
      timeAnalysisList: timeAnalysisList,
    }
    setJobResult(result)

    return result
  }

  /**
   * Handler of the click on a Download link.
   */
  const onDownloadClick = async () => {
    if (!isLoading) {
      setLoading(true)

      try {
        const result = jobResult ?? (await getJobResult())
        logger.debug('Job result:', result)
        if (result !== undefined) {
          if (module) {
            await module.downloadResult(result)
          } else {
            await ModuleConfig.defaultDownloadResult(result)
          }
        }
      } catch (error) {
        if (error instanceof Error) {
          setErrorMsg(error.message)
        } else {
          setErrorMsg(String(error)) // fallback for non-Error values
        }
      }
      setLoading(false)
    }
  }

  /**
   * Handler of the click on the link to show the Time Analysis charts.
   */
  const onViewChartClick = async () => {
    setLoadingCharts(true)
    logger.debug(LogTag.TaskList, 'onViewChartClick')

    if (timeAnalysisSeries?.length > 0) {
      props.showChartsDialogVisible(timeAnalysisSeries)
    } else {
      const result = jobResult ?? (await getJobResult())

      // Get data if needed
      if (result.timeAnalysisList && result.timeAnalysisList.length > 0) {
        let resultChangeCount = 0

        // Process all data checking in parallel
        await Promise.all(
          result.timeAnalysisList.map(async (analysis) => {
            if (undefined == analysis.data) {
              // Data not loaded yet: download them from the back-end
              const session = await fetchAuthSession()
              const analysisText = await downloader.asyncGetContentFromIas(
                session,
                analysis.timeAnalysisIasId
              )
              const json = JSON.parse(analysisText)
              ++resultChangeCount
              analysis.data = json
            }
          })
        )

        logger.debug(LogTag.TaskList, 'onViewChartClick result:', result)
        if (resultChangeCount > 0) {
          setJobResult(result)
        }

        // Create chart series
        const seriesList: TimeAnalysisSeries[] = []
        for (const timeData of result.timeAnalysisList) {
          const series = new TimeAnalysisSeries(
            timeData.data?.timestamps,
            timeData.data?.aiProbability,
            timeData.inputFileName,
            timeData.isAi
          )
          seriesList.push(series)
        }
        setTimeAnalysisSeries(seriesList)
        props.showChartsDialogVisible(seriesList)
      }
    }

    setLoadingCharts(false)
  }

  return (
    <>
      <tr className="job-info" data-process-job-id={props.job?.job_id}>
        <td>{startDateTime.toLocaleString()}</td>
        <td className="truncate">
          {props.job?.job_input_args?.job_label ??
            uploadedFileName ??
            props.job?.job_id}
        </td>
        <td>{moduleDisplayName}</td>
        <td>
          <StatusComp status={props.job.job_status} />
        </td>
        <td>
          {/* Display job cost if available */}
          {props.job.job_status !== 'error' && (
            <div>
              {props.job.job_cost !== undefined && (
                <>
                  <BiCoin />
                  {Math.round(Number(props.job.job_cost * 100)) / 100}
                </>
              )}
            </div>
          )}
        </td>
        <td className="resultCol">
          {!errorMsg &&
            props.job?.job_status !== 'error' &&
            props.job?.job_status !== 'running' &&
            props.job?.job_status !== 'created' && (
              <>
                {getDatesDiffAsDays(props.job?.job_end_datetime) >= 7 ? (
                  <div className="text-gray-300">Expired</div>
                ) : (
                  <div className="download">
                    <IconButton
                      onClick={onDownloadClick}
                      isLoading={isLoading}
                      icon={BiDownload}
                      label="Download"
                    />

                    {props.job.job_input_args?.timeAnalysis == true && (
                      <IconButton
                        onClick={onViewChartClick}
                        isLoading={isLoadingCharts}
                        icon={BiLineChart}
                        label="View charts"
                      />
                    )}
                  </div>
                )}
              </>
            )}
          {(props.job?.job_status === 'error' || errorMsg) && (
            <JobError jobId={props.job?.job_id} message={errorMsg} />
          )}
        </td>
      </tr>
    </>
  )
}

export default JobInfo
